import { useState } from "react";

// Material Dashboard 2 PRO React components
import { InputLabel, Autocomplete, TextField } from "@mui/material";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDAvatar from "components/MDAvatar";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import PictureCropper from "components/PictureCropper/PictureCropper";
import sampleProfilePhoto from "assets/images/sample.png";

const UserDetailsForm = ({
  formProps,
  selectedTeam,
  setImage,
  profilePicture,
  setProfilePicture,
  rolesList,
  timeAtCompanyOptions,
  averageSalesCycleLengthOptions,
  fieldsToInclude = {
    name: true,
    role: true,
    timeAtCompany: true,
    averageSalesCycleLength: true,
    profilePicture: true,
  },
}) => {
  const { values, setFieldValue, touched, errors } = formProps;

  const [imgSrc, setImgSrc] = useState("");
  const [isCropperOpen, setIsCropperOpen] = useState(false);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsCropperOpen(true);
      const reader = new FileReader();
      reader.addEventListener("load", () => setImgSrc(reader.result?.toString() || ""));
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleAfterCrop = (blob) => {
    if (!blob) return;
    setImage(blob);
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = function () {
      setProfilePicture(reader.result);
      setIsCropperOpen(false);
    };
  };

  const handleCropCancel = () => {
    setProfilePicture("");
    setImgSrc("");
    setIsCropperOpen(false);
  };

  const removeProfilePicture = async () => {
    setProfilePicture("");
    setImgSrc("");
  };

  return (
    <>
      {fieldsToInclude?.name ? (
        <MDBox mb={2}>
          <MDInput
            fullWidth
            variant="standard"
            name="name"
            label="Name"
            value={values.name}
            onChange={(event) => {
              setFieldValue("name", event.target.value);
            }}
            helperText={touched.name && errors.name}
            error={touched.name && Boolean(errors.name)}
          />
        </MDBox>
      ) : null}
      {!fieldsToInclude?.role || selectedTeam ? null : (
        <MDBox mb={2}>
          <Autocomplete
            fullWidth
            disableClearable
            id="role"
            options={rolesList}
            value={values.role || {}}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            onChange={(_, option) => {
              setFieldValue("role", option || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Role"
                variant="standard"
                error={touched.role && Boolean(errors.role)}
                helperText={touched.role && (errors?.role?.value || errors?.role)}
              />
            )}
          />
        </MDBox>
      )}
      {fieldsToInclude?.timeAtCompany ? (
        <MDBox mb={2}>
          <Autocomplete
            fullWidth
            id="timeAtCompany"
            options={timeAtCompanyOptions}
            value={values.timeAtCompany || {}}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            onChange={(_, option) => {
              setFieldValue("timeAtCompany", option || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Time at company"
                variant="standard"
                error={touched.timeAtCompany && Boolean(errors.timeAtCompany)}
                helperText={
                  touched.timeAtCompany && (errors?.timeAtCompany?.value || errors.timeAtCompany)
                }
              />
            )}
          />
        </MDBox>
      ) : null}
      {fieldsToInclude?.averageSalesCycleLength ? (
        <MDBox mb={2}>
          <Autocomplete
            fullWidth
            id="averageSalesCycleLength"
            options={averageSalesCycleLengthOptions}
            value={values?.averageSalesCycleLength || {}}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            onChange={(_, option) => {
              setFieldValue("averageSalesCycleLength", option || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Average sales cycle length"
                error={touched.averageSalesCycleLength && Boolean(errors.averageSalesCycleLength)}
                helperText={
                  touched.averageSalesCycleLength &&
                  (errors?.averageSalesCycleLength?.value || errors.averageSalesCycleLength)
                }
              />
            )}
          />
        </MDBox>
      ) : null}
      {fieldsToInclude?.profilePicture ? (
        <>
          <MDBox mb={2} mt={4}>
            <MDTypography variant="body">
              <InputLabel>Your profile photo ( optional )</InputLabel>
            </MDTypography>
            <MDTypography variant="caption">
              Upload your photo to be recognized on leaderboards
            </MDTypography>
          </MDBox>
          <MDBox mb={2} sx={{ display: "flex", alignItems: "center" }}>
            <MDAvatar
              variant="circular"
              htmlFor="profile-image"
              lable="profile-pic"
              src={profilePicture || sampleProfilePhoto}
              alt="profile-image"
              size="xl"
              component="label"
              shadow="sm"
              sx={{
                img: {
                  height: "100%",
                },
                cursor: "pointer",
              }}
            />
            <MDBox ml={4}>
              <MDTypography variant="body">
                <MDButton
                  htmlFor="upload-image-input"
                  variant="outlined"
                  component="label"
                  color="secondary"
                  sx={{ cursor: "pointer" }}
                >
                  Upload Image
                </MDButton>
              </MDTypography>
              <input
                name="profileImage"
                id="upload-image-input"
                type="file"
                accept="image/*"
                onChange={onSelectFile}
                onClick={(e) => {
                  e.target.value = null;
                }}
                hidden
              />
            </MDBox>
            {profilePicture ? (
              <MDBox ml={1}>
                <MDButton
                  variant="outlined"
                  component="label"
                  color="secondary"
                  onClick={removeProfilePicture}
                >
                  Remove Image
                </MDButton>
              </MDBox>
            ) : null}
          </MDBox>

          <PictureCropper
            imgSrc={imgSrc}
            open={isCropperOpen}
            handleAfterCrop={handleAfterCrop}
            handleCancel={handleCropCancel}
            onClose={handleCropCancel}
          />
        </>
      ) : null}
    </>
  );
};

export default UserDetailsForm;
